import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getAnimalById } from '../../api/animalsApi';
import { discard } from '../../api/inventoryApi';
import { getSpecimensByOwnership } from '../../api/specimensApi';
import { Loader, Tooltip } from '../../components';
import BackButton from '../../components/back-button/BackButton';
import SpecimenLotInfo from '../../components/specimen-lot-info/SpecimenLotInfo';
import {
  ANIMAL_CONSTANTS,
  BUTTON_CONSTANTS,
  INVENTORY_CONSTANTS,
  INVENTORY_MANAGEMENT_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { useAppSelector } from '../../redux/hooks';
import { showToast } from '../../services/toast.service';
import { Animal, DiscardRequest, SpecimenInventory } from '../../types/interfaces';
import { Genders, TransactionStatusEnum } from '../../types/enums';
import { InfoSVG } from '../../components/svgs';
import { pickInventoryListState } from './Inventory';

type DiscardItem = {
  availableQty: number;
  isSelected: boolean;
  quantity: number;
  specimenId: number;
  embryoStrawId?: number;
};

const InventoryDiscard = (): JSX.Element => {
  const { animalId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const listNavigationState = pickInventoryListState(location.state);

  const returnToInventoryItem = () => {
    if (animalId) {
      navigate(`${ROUTE_PATHS.APP_INVENTORY}/${animalId}`, { state: listNavigationState });
    } else {
      navigate(ROUTE_PATHS.APP_INVENTORY, { state: listNavigationState });
    }
  };

  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const user = useAppSelector(state => state.user);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notes, setNotes] = useState<string>('');
  const [ownership, setOwnership] = useState<SpecimenInventory[]>();
  const [selectedDiscardItems, setSelectedDiscardItems] = useState<DiscardItem[]>([]);
  const [totalAvailableOwnedQty, setTotalAvailableOwnedQty] = useState<number>();
  const [animal, setAnimal] = useState<Animal>();

  useEffect(() => {
    if (animalId) {
      fetchAnimal();
    } else {
      setAnimal(undefined);
    }
  }, [animalId]);

  const fetchAnimal = async () => {
    try {
      const response = await getAnimalById(animalId ?? '');
      setAnimal(response.data);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const isFemale = animal?.gender === Genders.Female;

  const totalDiscardQuantity = selectedDiscardItems
    .filter(item => item.isSelected)
    .reduce((accumulator, current) => accumulator + current.quantity, 0);

  useEffect(() => {
    const handleGetSpecimens = async () => {
      if (animalId && accountId && animal) {
        try {
          const params = {
            include: 'Animal, SpecimenType, EmbryoStraw, SpecimenCustomDataValue',
            userId: user.userId,
            getUpdatedAvailableQuantities: true,
            excludeStatuses: [TransactionStatusEnum.Pending, TransactionStatusEnum.Hold, TransactionStatusEnum.Processed],
          };

          const { data } = await getSpecimensByOwnership(animalId, accountId, params);

          let flattenedOwnership: SpecimenInventory[] = [];
          if (animal?.gender?.toLowerCase() === 'female') {
            flattenedOwnership = data.flatMap((specimen: SpecimenInventory) => {
              if (specimen.embryoStraws && specimen.embryoStraws.length > 0) {
                return specimen.embryoStraws.map(straw => ({
                  ...specimen,
                  embryoStrawCode: straw.embryoStrawCode,
                  embryoStrawNumber: straw.embryoStrawNumber,
                  embryoStrawId: straw.embryoStrawId,
                  quantityAvailable: straw.embryoCountPerStraw ?? 0,
                  embryoGrade: straw.embryoGrade,
                  embryoStage: straw.embryoStage,
                }));
              }
              return specimen;
            });
          } else {
            flattenedOwnership = data;
          }

          setOwnership(flattenedOwnership);
          setTotalAvailableOwnedQty(flattenedOwnership.reduce((acc, curr) => acc + curr.quantityAvailable, 0));
          handleCreateDiscardItems(flattenedOwnership);
          setIsLoading(false);
        } catch (error) {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      }
    };

    handleGetSpecimens();
  }, [animalId, accountId, animal]);

  const handleCreateDiscardItems = (ownership: SpecimenInventory[]) => {
    const discardItems = ownership.map(item => {
      return {
        isSelected: false,
        specimenId: item.specimenId,
        embryoStrawId: item.embryoStrawId,
        quantity: 0,
        availableQty: item.quantityAvailable,
      };
    });
    setSelectedDiscardItems(discardItems);
  };

  const isValid = (isSubmit?: boolean) => {
    if (!selectedDiscardItems.find(item => item.isSelected)) {
      isSubmit && showToast.error(toastMessages.NO_ITEM_SELECTED);
      return false;
    } else if (selectedDiscardItems.some(item => item.isSelected && item.quantity > item.availableQty)) {
      isSubmit && showToast.error(toastMessages.INVALID_DISCARD_QUANTITY);
      return false;
    } else if (!totalAvailableOwnedQty || totalDiscardQuantity > totalAvailableOwnedQty) {
      isSubmit && showToast.error(toastMessages.INVALID_DISCARD_QUANTITY);
      return false;
    } else if (totalDiscardQuantity === 0 || selectedDiscardItems.some(item => item.isSelected && item.quantity === 0)) {
      isSubmit && showToast.error(toastMessages.INVALID_ZERO_QUANTITY);
      return false;
    }
    return true;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isValid(true)) {
      try {
        setIsLoading(true);
        let newSelectedDiscardItems = selectedDiscardItems.filter(item => item.isSelected);
        const mappedDiscardSpecimens: DiscardRequest[] = newSelectedDiscardItems.map(item => {
          const discardEntry: DiscardRequest = {
            specimenId: item.specimenId,
            quantity: item.quantity,
          };

          if (isFemale && item.embryoStrawId) {
            discardEntry.selectedEmbryoStrawIds = [item.embryoStrawId];
          }

          return discardEntry;
        });
        if (!accountId) {
          throw new Error('Account Id is not defined');
        }
        await discard(accountId, mappedDiscardSpecimens, notes);
        showToast.success(toastMessages.DISCARD_SUCCESSFULLY_REQUESTED);
        returnToInventoryItem();
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const inputCheckbox = (discardItem: DiscardItem | undefined, index: number) => {
    return (
      <input
        type="checkbox"
        name="selected-checkin-specimen-row"
        disabled={isFemale}
        onChange={e =>
          setSelectedDiscardItems(prev => {
            const newItems = [...prev];
            if (discardItem) {
              const updatedItem = { ...discardItem, isSelected: e.target.checked };
              if (isFemale && e.target.checked) {
                updatedItem.quantity = discardItem.availableQty;
              }

              newItems[index] = updatedItem;
            }
            return newItems;
          })
        }
        checked={discardItem?.isSelected || false}
      />
    );
  };

  const inputQuantityElement = (discardItem: DiscardItem | undefined, index: number) => {
    const displayValue = isFemale
      ? discardItem?.availableQty ?? '-'
      : discardItem?.isSelected
      ? discardItem.quantity > 0
        ? discardItem?.quantity
        : ''
      : '-';

    return (
      <input
        type="text"
        placeholder="0"
        value={displayValue}
        disabled={isFemale || !discardItem?.isSelected}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (isFemale) return;
          const count = +e.target.value.replace(/[^\d]+/, '').replace(/^0+/, '');
          let newQuantities = [...selectedDiscardItems];
          newQuantities[index] = { ...selectedDiscardItems[index], quantity: count };
          setSelectedDiscardItems(newQuantities);
        }}
      />
    );
  };

  const handleErrors = (discardItem: DiscardItem | undefined, item: SpecimenInventory) => {
    return discardItem?.isSelected
      ? (discardItem?.quantity ?? 0) > item?.quantityAvailable
        ? 'error'
        : discardItem?.quantity === 0
        ? 'warning'
        : ''
      : '';
  };

  return (
    <div className="inventory-discard">
      <BackButton onClick={returnToInventoryItem} />
      <div className="inventory-action card">
        <h1>{PAGE_HEADER_CONSTANTS.DISCARD_INVENTORY}</h1>
        {isLoading ? (
          <Loader loaderSize="medium" simple />
        ) : (
          <form action="submit" onSubmit={e => handleSubmit(e)}>
            <h2>{ANIMAL_CONSTANTS.ANIMAL_INFO}</h2>
            <p className="animal-info-text">
              {ANIMAL_CONSTANTS.ANIMAL_CODE}: {ownership?.length ? ownership[0].animal?.code : ''}
            </p>
            <p className="animal-info-text">
              {ANIMAL_CONSTANTS.ANIMAL_NAME}: {ownership?.length ? ownership[0].animal?.name : ''}
            </p>
            <p className="animal-info-text">
              {LABEL_CONSTANTS.QUANTITY_AVAILABLE}: {totalAvailableOwnedQty ?? ''}
            </p>
            <h2>{LABEL_CONSTANTS.SELECT_ITEM_QUANTITY}</h2>
            {isFemale && (
              <div className="info-banner">
                <InfoSVG size="18" color="#0f64dcf5" />
                {INVENTORY_CONSTANTS.EMBRYO_AUDIT_IN_PROGRESS_MESSAGE}
              </div>
            )}
            <div className="desk-select-specimens">
              <div className="specimen-table">
                <table>
                  <thead>
                    <tr>
                      <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                      <th>{TABLE_HEADER_CONSTANTS.ANIMAL}</th>
                      <th>{TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}</th>
                      <th>{TABLE_HEADER_CONSTANTS.LOT_NO}</th>
                      {isFemale ? (
                        <>
                          <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                          <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                          <th>{TABLE_HEADER_CONSTANTS.FLUSH_MATING}</th>
                        </>
                      ) : (
                        <th>{TABLE_HEADER_CONSTANTS.QUALITY}</th>
                      )}
                      <th>{isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
                      <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ownership
                      ?.filter((item: SpecimenInventory) => {
                        if (isFemale && !item.embryoStrawId) return false;
                        return true;
                      })
                      .map((item: SpecimenInventory, index: number) => {
                        const discardItem = selectedDiscardItems.find(discardItem =>
                          isFemale
                            ? discardItem.embryoStrawId === item.embryoStrawId
                            : discardItem.specimenId === item.specimenId,
                        );
                        const flushMating: string = String(
                          item.specimenCustomDataValues?.find(val => val.specimenCustomDataKey?.keyName === 'Flush Mating')
                            ?.value ?? '-',
                        );

                        return (
                          <tr key={index} className={handleErrors(discardItem, item)}>
                            <td>{inputCheckbox(discardItem, index)}</td>
                            <td>
                              {item.animal?.code}-{item.animal?.name}
                            </td>
                            <td>{item.specimenType?.name}</td>
                            <td>{<SpecimenLotInfo specimenId={item.specimenId} lotNumber={item?.lotNumber} />}</td>
                            {isFemale ? (
                              <>
                                <td>
                                  <div>
                                    {item.embryoStrawCode ?? '-'}
                                    <Tooltip>
                                      {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${item.embryoGrade || 'NA'}`} <br />
                                      {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${item.embryoStage || 'NA'}`}
                                    </Tooltip>
                                  </div>
                                </td>
                                <td>{item.embryoStrawNumber ?? '-'}</td>
                                <td>{flushMating}</td>
                              </>
                            ) : (
                              <td>{item.quality}</td>
                            )}

                            <td>{item.quantityAvailable}</td>
                            <td>{inputQuantityElement(discardItem, index)}</td>
                          </tr>
                        );
                      })}
                    <tr>
                      <td colSpan={8}></td>
                      <td colSpan={3}>
                        <div className="quantity">{totalDiscardQuantity}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/** Mobile View */}
            <div className="xs-select-specimens">
              {ownership &&
                ownership
                  ?.filter(item => {
                    if (isFemale && !item.embryoStrawId) return false;
                    return true;
                  })
                  .map((item, index) => {
                    const discardItem = selectedDiscardItems.find(discardItem =>
                      isFemale ? discardItem.embryoStrawId === item.embryoStrawId : discardItem.specimenId === item.specimenId,
                    );
                    const flushMating: string = String(
                      item.specimenCustomDataValues?.find(val => val.specimenCustomDataKey?.keyName === 'Flush Mating')?.value ??
                        '-',
                    );

                    return (
                      <div
                        className={`card max-width ${handleErrors(discardItem, item)}`}
                        key={`specimen-row:${item.specimenId}-straw:${item.embryoStrawId ?? 'none'}-index:${index}`}>
                        <div className="selected-specimens-row">
                          <div className="input-check">{inputCheckbox(discardItem, index)}</div>
                          <div className="select-specimens">
                            <div className="card-content-section">
                              <div className="left column">
                                <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                                <label>{`${item.animal?.code} - ${item.animal?.name}`}</label>
                              </div>
                              <div className="left column">
                                <label>{`${TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}: `}</label>
                                <label>{`${item.specimenType?.name}`}</label>
                              </div>
                              <div className="left column">
                                <label>{`${TABLE_HEADER_CONSTANTS.LOT_NO}: `}</label>
                                <label>{<SpecimenLotInfo specimenId={item.specimenId} lotNumber={item?.lotNumber} />}</label>
                              </div>
                              <div className="right column">
                                <label>{`${TABLE_HEADER_CONSTANTS.FLUSH_MATING}: `}</label>
                                <label>{flushMating}</label>
                              </div>
                            </div>
                            <div className="card-content-section">
                              {isFemale ? (
                                <>
                                  <div className="left column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.STRAW_LOT}: `}</label>
                                    <label>
                                      {`${item.embryoStrawCode ?? '-'}`}{' '}
                                      {isFemale && (
                                        <Tooltip>
                                          {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${item.embryoGrade || 'NA'}`} <br />
                                          {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${item.embryoStage || 'NA'}`}
                                        </Tooltip>
                                      )}
                                    </label>
                                  </div>
                                  <div className="left column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.STRAW_NUMBER}: `}</label>
                                    <label>{`${item.embryoStrawNumber ?? '-'}`}</label>
                                  </div>
                                </>
                              ) : (
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.QUALITY}: `}</label>
                                  <label>{`${item.quality}`}</label>
                                </div>
                              )}
                              <div className="left column ">
                                <label>
                                  {`${isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}: `}
                                </label>
                                <label>{`${item.quantityAvailable}`}</label>
                              </div>
                              <div className="right center column">
                                <label>{`${TABLE_HEADER_CONSTANTS.QUANTITY}:`}</label>
                                {inputQuantityElement(discardItem, index)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              <div className="total-quantity">
                <label>{LABEL_CONSTANTS.TOTAL_QUANTITY}: &nbsp; </label>
                {totalDiscardQuantity}
              </div>
            </div>
            <br />
            <div className="form-row expanded-text-area">
              <label>{LABEL_CONSTANTS.NOTES}:</label>
              <textarea id="notes" name="notes" placeholder="Notes" onChange={e => setNotes(e.target.value)} rows={4} cols={50} />
            </div>
            <br />
            <div className="button-container">
              <p className="discard-submit-guide">{INVENTORY_MANAGEMENT_CONSTANTS.COMMON_INVENTORY_FOOTER}</p>
              <button className={isValid() ? 'button green small' : 'button green small disabled'}>
                {BUTTON_CONSTANTS.SUBMIT}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InventoryDiscard;
